import { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";

const MIN_VISIBLE_MS = 200;
const FADE_OUT_MS = 600;

const PageTransitionOverlay = () => {
  const location = useLocation();
  const [phase, setPhase] = useState<"visible" | "fading" | "hidden">("visible");
  const showTimeRef = useRef(Date.now());
  const prevPathRef = useRef(location.pathname);
  const isInitialRef = useRef(true);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const minTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const hide = useCallback(() => {
    const elapsed = Date.now() - showTimeRef.current;
    const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);

    clearTimeout(minTimerRef.current);
    minTimerRef.current = setTimeout(() => {
      setPhase("fading");
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = setTimeout(() => setPhase("hidden"), FADE_OUT_MS);
    }, remaining);
  }, []);

  // Initial load: hide after page is ready
  useEffect(() => {
    if (!isInitialRef.current) return;
    isInitialRef.current = false;

    const ready = () => {
      // Wait for fonts + a rAF to ensure layout is stable
      if (document.fonts?.ready) {
        document.fonts.ready.then(() => requestAnimationFrame(() => hide()));
      } else {
        requestAnimationFrame(() => hide());
      }
    };

    if (document.readyState === "complete") {
      ready();
    } else {
      window.addEventListener("load", ready, { once: true });
      return () => window.removeEventListener("load", ready);
    }
  }, [hide]);

  // Route changes: show overlay, then hide when mounted
  useEffect(() => {
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    clearTimeout(fadeTimerRef.current);
    clearTimeout(minTimerRef.current);
    showTimeRef.current = Date.now();
    setPhase("visible");

    // Wait a tick for the new page component to mount, then hide
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (document.fonts?.ready) {
          document.fonts.ready.then(() => requestAnimationFrame(() => hide()));
        } else {
          requestAnimationFrame(() => hide());
        }
      });
    });
  }, [location.pathname, hide]);

  if (phase === "hidden") return null;

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{
        width: "100vw",
        height: "100vh",
        background: "linear-gradient(180deg, #0B0B0C 0%, #121214 100%)",
        pointerEvents: phase === "fading" ? "none" : "all",
        opacity: phase === "fading" ? 0 : 1,
        transition:
          phase === "fading"
            ? `opacity ${FADE_OUT_MS}ms cubic-bezier(0.16, 1, 0.30, 1)`
            : "none",
      }}
    >
      <span
        className="font-sans uppercase select-none text-[42px] sm:text-[56px] md:text-[64px] lg:text-[72px] tracking-[0.35em] font-medium"
        style={{
          color: "#A8A9AD",
          animation:
            reducedMotion || phase === "fading"
              ? "none"
              : "motai-pulse 2000ms ease-in-out infinite",
        }}
      >
        MOTAI
      </span>
    </div>
  );
};

export default PageTransitionOverlay;
